import { Component, OnInit, HostListener, Input, Output, EventEmitter, ViewChild, ElementRef, ViewEncapsulation, ChangeDetectionStrategy} from "@angular/core";
import {conditionalValidator} from '@irb-common/validators/conditionalValidator';
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { DropdownSearchGridLayoutService } from "@irb-common/components/dropdown-search/dropdown-search.grid-layout.service";
import { IDropdownItem } from "@irb-common/models/dropdown.model";
//import { AllModules, Module } from 'ag-grid-enterprise';
import { HoverPanelService } from "@irb-common/components/hover-panel/hover-panel.service";
import { TooltipService } from '@irb-common/components/tooltip/tooltip.service';
import { DecodeHtmlPipe } from '@irb-common/pipes/DecodeHtmlPipe';


@Component({ standalone: false,
  selector: "irb-dropdown-search",
  templateUrl: "./dropdown-search.component.html",
  styleUrls: ["./dropdown-search.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [DecodeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DropdownSearchComponent implements OnInit {
  @ViewChild("input") input: ElementRef;
  private _isRequired: boolean = false;
  private _value: IDropdownItem = null;
  private _disabled: boolean = false;
  @Input() placeholder: string = "-Search-";
  @Input() size: number = 25;
  @Input() hoverPanelZIndex: number;
  @Input() autoFocus: boolean = false;
  @Input() addNewItem: any;
    @Input() hoverPanelWidth: number = 300;
  @Input() threshold: number = 1;
  @Output() valueChange = new EventEmitter<IDropdownItem>();
  @Output() search = new EventEmitter<string>();
  @Output() selection = new EventEmitter<IDropdownItem>();
  @Output() clearSelection = new EventEmitter<void>();
  @Input() editIconOnHover: boolean = false;

  @Input()
  set isRequired(value: boolean) {
    this._isRequired = value || false;
    this.applyValidators();
  }

  get isRequired(): boolean {
    return this._isRequired;
  }

  inputControl = new FormControl();
  grid: any;
  gridOptions: any;
  gridHeight: string = "";
  isVisible: boolean = false;
  searchString: string;
  selectedRowIndex: number;
  //modules: Module[] = AllModules;
  showAddNewButton: boolean = false;
  @Output() openNewItemFlow = new EventEmitter<any>();
  frameworkComponents: any;

  constructor(private gls: DropdownSearchGridLayoutService, private hps: HoverPanelService, private tts: TooltipService, private dhp: DecodeHtmlPipe) {
  }

  ngOnInit() {
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(),      
      suppressMovableColumns: true,
      gridAutoHeight: false,
      overlayLoadingTemplate: "<div class='loading-spinner'></div>",
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No results found.</span>",
      rowSelection: "single",
    };
    
    this.frameworkComponents = this.gls.getFrameworkComponents();

    this.inputControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((val) => {
      if ((!val && this.value?.name) || val?.length < this.threshold) {
        delete this.value;
        this.grid?.setGridOption('rowData', []);
        this.clearSelection.emit();
        this.searchString = "";
        return;
      }

      if ((this.value == null || val !== this.value.name) && val != null && val?.length >= this.threshold) {
        this.onValueUpdate(val);
      }
    });

    this.setInputValue();
    this.setDisabled();

    if(this.autoFocus) {
      this.focusInput();
    }
  }

  @Input() set value(v: IDropdownItem) {
    this._value = v;
    this.setInputValue();
  }

  get value(): IDropdownItem {
    return this._value;
  }

  @Input() set disabled(v: boolean) {
    this._disabled = v;
    this.setDisabled();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  onGridReady(params: any) {
    this.grid = params.api;

    if(this.isVisible) {
      this.onValueUpdate(this.inputControl.value);
      this.selectDefaultRow();
      //this.grid.sizeColumnsToFit();
      this.updateGridHeight();
    }
  }

  clearInput() {
    this.setValue(null);
    this.searchString = "";
    if (this.addNewItem) {
      this.showAddNewButton = true;
    }
    this.clearSelection.emit();
  }

  focusInput() {
    setTimeout(() => {
      this.input.nativeElement.focus();
      const s = typeof this.inputControl.value === "string" ? this.inputControl.value : "";
      //this.input.nativeElement.setSelectionRange(s.length, s.length);
      this.input.nativeElement.select();
      this.input.nativeElement.click();
    });
  }

  hide() {
    this.isVisible = false;
    this.tts.remove();
    this.hps.remove();
    this.grid = null;
    this.setInputValue();
  }

  onRowClicked(e: any) {
    this.hide();
    this.setValue(e.node.data);
    this.selection.emit(this.value);
  }

  setValue(n: IDropdownItem) {
    this.value = n;
    this.valueChange.emit(this.value);
    this.setInputValue();
  }

  setInputValue() {
    if(this.value && this.value.name) {
      this.value.name = this.dhp.transform(this.value.name);
    }

    this.inputControl.setValue(this.value?.name || null);
  }

  onValueUpdate(s: any) {
    this.showAddNewButton = false;
    if (s?.length > 0) {
      this.isVisible = true;

      if (this.grid && this.searchString !== s) {
        this.grid.setGridOption("rowData", []);
        this.grid.showLoadingOverlay();
      }

      this.searchString = s;
      this.search.emit(s);
    }
  }

  set(m: Array<IDropdownItem>) {
    m = m || [];
    m.forEach((obj: any) => {
      obj["match"] = this.searchString;
    });

    if (this.grid) {
      if (this.addNewItem && m.length === 0) {
        this.showAddNewButton = m.length === 0;
      }
      this.grid.setGridOption("rowData", m);
      this.selectDefaultRow();
      //this.grid.sizeColumnsToFit();
      this.updateGridHeight();
    }
  }

  selectDefaultRow() {
    this.grid?.forEachNode((node: any) => {
      if (node.data.value === this.value?.value) {
              this.selectRow(node.rowIndex);
          }
      });
  }

  incrementRowSelection() {
    this.selectRow(++this.selectedRowIndex);
  }

  decrementRowSelection() {
    this.selectRow(--this.selectedRowIndex);
  }

  selectRow(index: number) {
    if (index < 0) {
      index = 0;
    } else if (index > this.grid.getDisplayedRowCount() - 1) {
      index = this.grid.getDisplayedRowCount() - 1;
    }

    window.setTimeout(() => {
      if (index >= 0) {
        this.selectedRowIndex = index;
          if (this.grid) {
              this.grid.forEachNode((node: any) => node.setSelected(node.rowIndex === index));
              this.grid.ensureIndexVisible(index);
          }
      }
    }, 0);
  }

  onKeyUp(event: any) {
    switch (event.key) {
      case "ArrowDown":
        this.incrementRowSelection();
        break;
      case "ArrowUp":
        this.decrementRowSelection();
        break;
      case "Enter":
        const data = this.grid.getSelectedRows();
        this.hide();

        if (data.length > 0) {
          this.setValue(data[0]);
          this.selection.emit(data[0]);
        }
        break;
    }
  }

  setDisabled() {
    this._disabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  updateGridHeight() {
    const tr = this.grid.getDisplayedRowCount();

    if (tr === 0) {
      this.gridHeight = "90px";
    } else if (tr > 6) {
      this.gridHeight = "180px";
    } else {
      const p = tr * 30 + 3;
      this.gridHeight = p + "px";
    }

    this.hps.resize();
  }

  @HostListener("window:mousedown", ["$event"])
  onWindowClick(e: any) {
    if (this.input.nativeElement.contains(e.target)) {
      if(this.inputControl.value && typeof this.inputControl.value === "string" && this.inputControl.value.length > 0) {
        this.onValueUpdate(this.inputControl.value);

        setTimeout(() => {
          if (this.input && this.input.nativeElement) {
            this.input.nativeElement.select();
          }
        }, 200);

      } else {
        this.hide();
      }
    }
  }

  onHoverPanelClose() {
    this.hide();
  }

  addNewItemClick() {
    let dataParam: any = {
      dropdownSearch: true,
      dropdownSearchValue: this.inputControl.value
    };
    this.openNewItemFlow.emit(dataParam);
    this.showAddNewButton = false;
    this.hide();
  }

  applyValidators() {
    if (this.inputControl && this.inputControl.setValidators) {
      const v = this.getValidators();
      this.inputControl.setValidators(v);
      this.inputControl.updateValueAndValidity();
    }
  }

  getValidators() {
    const v: any = [];
    
    v.push(conditionalValidator(() => this._isRequired, (c: FormControl) => {
      const hasValue = !(this.inputControl.value === null || typeof this.inputControl.value === "undefined" || this.inputControl.value == "");
      let r = null;

      if(c.touched) {
        r = !hasValue ? {'violatesRequired': {value: this.inputControl.value}} : null;
      }

      return r;
    }));

    return v;
  }
}
